"use client"

import styled from "@emotion/styled"
import { Button, TextField, Typography } from "@mui/material"
import { LottieRefCurrentProps } from "lottie-react"
import { useTranslations } from "next-intl"
import dynamic from "next/dynamic"
import Image from "next/image"
import React, { useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import "swiper/css/navigation"
import {
  AnimatedTypography,
  Desktop,
  Mobile,
  transitionDelayTime,
  transitionTime,
  TransitionUp,
  useInViewConfig,
} from "ui"
import Container from "ui/Container"
import CloseXIcon from "ui/icons/CloseXIcon"
import Search from "ui/icons/Search"
import { delayTime, transitionEase } from "ui/transitions/utils"
import {
  extractProperty,
  getNonUmbracoUrl,
  Link,
  replaceRichTextLinks,
} from "utils"
import { infoClick, pageStripFaq } from "utils/gtm"
import AccordionItemComp from "../Common/FaqItem"
import lottieAnimation from "../Homepage/FAQ/lottie.json"
import {
  VideoControlButton,
  videoControls,
} from "../Homepage/Hero/VideoControls"

const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

const OuterContainer = styled.div`
  width: 100%;
  position: relative;
`

const StyledLayout = styled.div`
  width: 100%;
  display: flex;
  gap: 82px;
  position: relative;
`

const StyledControlWrapper = styled.div`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
    position: absolute;
    top: 80px;
    inset-inline-end: 280px;
  }
`

const ContentLayout = styled.div`
  padding: 32px 16px 48px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 48px 0 64px;
  }
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;
`

const StyledLottie = styled(Lottie)`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: block;
    width: 325px;
    height: fit-content;
  }
`

const AccordionArray = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  /* max-width: 1040px; */
  gap: 24px;
`

const StyledButton = styled(Button)<{ target?: string }>`
  width: 100%;
  height: auto;
  min-height: 48px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 300px;
  }
`

const BoxTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "$inView",
})<{ $inView: boolean }>`
  width: 100%;
  opacity: ${(p) => (p.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
  transform: ${(p) => (p.$inView ? "translateX(0px)" : `translateX(-100vw)`)};
  transition-delay: ${delayTime}s;
  svg[aria-label="close"] {
    cursor: pointer;
  }
  label[data-shrink="true"] {
    transform: translate(10px, -30px) scale(0.75);
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 560px;
  }
`

const FAQLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  ${({ theme }) => theme.breakpoints.up("md")} {
    align-items: unset;
    gap: 24px;
  }
`
type UndStr = string | undefined

type Item = {
  question: string
  content: string
  defaultExpanded: boolean
  buttonLink?: string
  buttonText?: string
}

export default function AccordionFAQ({
  data,
  pageTitle,
}: {
  data: any
  pageTitle: string
}) {
  const t = useTranslations("Homepage")
  const title = extractProperty(data, "title") as UndStr
  const description = extractProperty(data, "description") as UndStr
  const fAQSearchLine = extractProperty(data, "fAQSearchLine") as UndStr
  const button = extractProperty(data, "button")
  const buttonText = button?.[0]?.name as UndStr
  const buttonLink = button?.[0]?.url as UndStr
  const externalLink = button?.[0]?.type?.toLowerCase() === "external"
  const itemsListBlock = extractProperty(data, "itemsList") || []
  const itemsList = itemsListBlock.map((el: any) => ({
    question: extractProperty(el, "question"),
    content: extractProperty(el, "answer"),
    buttonLink: extractProperty(el, "button")?.[0]?.url,
    buttonText: extractProperty(el, "button")?.[0]?.name,
  })) as Item[]
  const showSearchBox = extractProperty(data, "showSearchBox") as boolean

  const [searchQuery, setSearchQuery] = React.useState("")
  const filteredItems = searchQuery
    ? itemsList.filter(
        (item) =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.content.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : itemsList
  const { ref, inView } = useInView({ ...useInViewConfig, threshold: 0 })

  // Open the first accordion by default
  const [expandedIndex, setExpandedIndex] = useState<number>(-1)

  const lottieRef = useRef<LottieRefCurrentProps>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const handleAnimationControl = () => {
    if (lottieRef.current) {
      if (isPlaying) {
        lottieRef.current.pause()
      } else {
        lottieRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleChange =
    (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      // If expanding a new item, set its index; if collapsing, set to -1 (none)
      setExpandedIndex(isExpanded ? index : -1)

      // Track accordion interaction
      const clickedItem = filteredItems[index]
      if (clickedItem) {
        infoClick(
          clickedItem.question, // click_text: The text displayed on the button that was clicked
          isExpanded ? "פתיחה" : "סגירה", // sub_click_text: "פתיחה" או "סגירה" based on action
          pageTitle, // category1: Main parent menu name
          title || "", // element_name: Component name from title or fallback
        )
      }
    }

  return (
    <OuterContainer ref={ref}>
      <Container>
        <StyledLayout>
          <ContentLayout>
            {title || description ? (
              <BoxTitle>
                <Desktop>
                  <AnimatedTypography
                    variant="h3"
                    component={"h2"}
                    $inview={inView}
                  >
                    {title}
                  </AnimatedTypography>
                </Desktop>
                <Mobile>
                  <AnimatedTypography variant="h2" $inview={inView}>
                    {title}
                  </AnimatedTypography>
                </Mobile>
                {description && (
                  <AnimatedTypography
                    variant="body1"
                    $inview={inView}
                    component="div"
                    dangerouslySetInnerHTML={{
                      __html: replaceRichTextLinks(description),
                    }}
                  ></AnimatedTypography>
                )}
              </BoxTitle>
            ) : null}
            {showSearchBox && (
              <StyledTextField
                $inView={inView}
                variant="filled"
                label={fAQSearchLine}
                aria-label="חיפוש"
                slotProps={{
                  input: {
                    endAdornment: searchQuery ? (
                      <CloseXIcon onClick={() => setSearchQuery("")} />
                    ) : (
                      <Search />
                    ),
                    inputProps: {
                      "aria-label": "חיפוש",
                    },
                  },
                }}
                value={searchQuery}
                onChange={(e: any) => setSearchQuery(e.target.value)}
              />
            )}
            <FAQLayout>
              {searchQuery && filteredItems.length > 0 ? (
                <Typography variant="body1">
                  {(() => {
                    const translated = t("weFound10Search")
                    const parts = translated.split("10")
                    return (
                      <>
                        {parts[0]}
                        <span style={{ fontWeight: 700 }}>
                          {filteredItems.length}
                        </span>
                        {parts[1]}
                      </>
                    )
                  })()}
                </Typography>
              ) : null}
              {searchQuery && filteredItems.length === 0 ? (
                <Typography variant="body1">{t("noResultsFound")}</Typography>
              ) : null}
              {searchQuery && filteredItems.length === 0 ? (
                <>
                  <Desktop>
                    <Image
                      src="/images/noFAQ.png"
                      alt="no-results"
                      width={324}
                      height={324}
                    />
                  </Desktop>
                  <Mobile>
                    <Image
                      src="/images/noFAQ.png"
                      alt="no-results"
                      width={120}
                      height={120}
                    />
                  </Mobile>
                </>
              ) : (
                <AccordionArray>
                  {filteredItems.map((item, i) => (
                    <TransitionUp
                      $inView={inView}
                      $delay={transitionDelayTime * 0.5 * i}
                      key={i}
                    >
                      <AccordionItemComp
                        content={item.content}
                        title={item.question}
                        buttonLink={item.buttonLink}
                        buttonText={item.buttonText}
                        expanded={expandedIndex === i}
                        onChange={handleChange(i)}
                        pageTitle={pageTitle}
                        accordionTitle={title}
                      />
                    </TransitionUp>
                  ))}
                  {buttonLink && (
                    <StyledButton
                      variant="primary"
                      size="medium"
                      LinkComponent={Link}
                      onClick={() => {
                        pageStripFaq(
                          buttonText || "",
                          pageTitle || "",
                          title || "",
                        )
                      }}
                      href={getNonUmbracoUrl(buttonLink)}
                      target={externalLink ? "_blank" : undefined}
                    >
                      {buttonText}
                    </StyledButton>
                  )}
                </AccordionArray>
              )}
            </FAQLayout>
          </ContentLayout>
          <StyledLottie
            animationData={lottieAnimation}
            lottieRef={lottieRef}
            autoplay={true}
            loop={true}
          />
          <StyledControlWrapper>
            <VideoControlButton
              icon={
                isPlaying
                  ? videoControls.desktop.pause
                  : videoControls.desktop.play
              }
              onClick={handleAnimationControl}
              ariaLabel={isPlaying ? "Pause" : "Play"}
              size={24}
            />
          </StyledControlWrapper>
        </StyledLayout>
      </Container>
    </OuterContainer>
  )
}
